import { isValidEmail } from '@felix/domain';
import { Button, Modal, SegmentedControl, Textarea, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import type { SupplierSource } from './source.js';
import type { Supplier } from './types.js';

export interface SupplierPortalDialogProps {
  mode: 'invite' | 'revoke';
  supplier: Supplier;
  portal: NonNullable<SupplierSource['portal']>;
  onClose(): void;
  onDone(message: string): void;
}

/**
 * Tedarikçi portalı daveti ve iptali (ADR 0112 §5). Davet bağlantısı 24 saat geçerlidir ve
 * e-postayla gider; yeniden davet önceki bağlantıyı geçersiz kılar. İptal gerekçe ister ve hesap bu
 * şirkete anında erişemez; geçmiş silinmez.
 */
export function SupplierPortalDialog({
  mode,
  supplier,
  portal,
  onClose,
  onDone,
}: SupplierPortalDialogProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.suppliers.portal.${key}`, options);
  const [email, setEmail] = useState(supplier.portal?.email ?? supplier.email ?? '');
  const [language, setLanguage] = useState<'en' | 'tr'>(
    i18n.resolvedLanguage === 'tr' ? 'tr' : 'en',
  );
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resend = supplier.portal?.status === 'invited';

  const submit = async () => {
    setFailure(null);
    if (mode === 'invite' && !isValidEmail(email.trim())) {
      setFieldError(k('emailInvalid'));
      return;
    }
    if (mode === 'revoke' && reason.trim().length < 5) {
      setFieldError(k('reasonShort'));
      return;
    }
    setFieldError(null);
    setBusy(true);
    try {
      if (mode === 'invite') {
        await portal.invite(supplier.id, { email: email.trim(), language });
        onDone(k('invited', { email: email.trim().toLowerCase() }));
      } else {
        await portal.revoke(supplier.id, reason.trim());
        onDone(k('revoked', { name: supplier.name }));
      }
    } catch (error) {
      const code = errorCode(error);
      setFailure(
        t(`purchasing.errors.${code}`, {
          defaultValue: k(`errors.${code}`, { defaultValue: t('purchasing.errors.GENERIC') }),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        mode === 'invite'
          ? k(resend ? 'resendTitle' : 'inviteTitle', { name: supplier.name })
          : k('revokeTitle', { name: supplier.name })
      }
      closeButtonProps={{ 'aria-label': t('purchasing.suppliers.dialog.cancel') }}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {mode === 'invite' ? (
          <>
            <p className={classes.dialogHint}>{k(resend ? 'resendText' : 'inviteText')}</p>
            <TextInput
              label={k('email')}
              type="email"
              required
              data-autofocus
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              error={fieldError}
              maxLength={320}
            />
            <div className={classes.dialogField}>
              <span className={classes.dialogLabel} id="supplier-portal-language">
                {k('language')}
              </span>
              <SegmentedControl
                aria-labelledby="supplier-portal-language"
                value={language}
                onChange={(value) => setLanguage(value === 'tr' ? 'tr' : 'en')}
                data={[
                  { value: 'en', label: 'English' },
                  { value: 'tr', label: 'Türkçe' },
                ]}
              />
            </div>
          </>
        ) : (
          <>
            <p className={classes.dialogHint}>{k('revokeText')}</p>
            <Textarea
              label={k('reason')}
              required
              data-autofocus
              autosize
              minRows={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.currentTarget.value)}
              error={fieldError}
            />
          </>
        )}
        {failure ? (
          <p className={classes.error} role="alert">
            {failure}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('purchasing.suppliers.dialog.cancel')}
          </Button>
          <Button type="submit" loading={busy} {...(mode === 'revoke' ? { color: 'red' } : {})}>
            {mode === 'invite' ? k(resend ? 'resend' : 'send') : k('revokeConfirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
